/**
 * src/admin-ui/src/pages/Billing.tsx
 *
 * Balance and account top-up page on the shared full-screen pattern
 * (components/edge-table): the balance fills the content area, the
 * top-up controls (amount, method, pay) live in the bottom bar.
 */

import React, { useEffect, useState } from "react";
import {
  Alert,
  Button,
  CircularProgress,
  MenuItem,
  Snackbar,
  TextField,
  Typography,
} from "@mui/material";
import { createYooMoneyInvoice, getBalance } from "../services/billing";
import { formatBalls } from "../utils/format";
import {
  BarTile,
  BottomBar,
  FullBleedViewport,
} from "../components/edge-table/EdgeTableLayout.styles";
import { BalanceState } from "./Billing.styles";

const paymentMethods = [{ label: "YooMoney", value: "yoomoney" }];

const Billing: React.FC = () => {
  const [balance, setBalance] = useState<number | null>(null);
  const [amount, setAmount] = useState("");
  const [method, setMethod] = useState("yoomoney");
  const [loading, setLoading] = useState(false);
  const [creatingPayment, setCreatingPayment] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchBalance = async () => {
      setLoading(true);
      try {
        const data = await getBalance();
        setBalance(data.balance);
      } catch (requestError) {
        console.error("Failed to fetch balance", requestError);
        setError("Не удалось загрузить баланс");
      } finally {
        setLoading(false);
      }
    };
    fetchBalance();
  }, []);

  const handlePay = async () => {
    const parsedAmount = Number.parseFloat(amount);
    if (!Number.isFinite(parsedAmount) || parsedAmount <= 0) {
      setError("Укажите сумму больше нуля");
      return;
    }

    setCreatingPayment(true);
    setError(null);
    try {
      if (method === "yoomoney") {
        const { payment_url: paymentUrl } =
          await createYooMoneyInvoice(parsedAmount);
        window.location.href = paymentUrl;
      }
    } catch (requestError) {
      console.error("Payment creation failed", requestError);
      setError("Не удалось создать платёж. Попробуйте позже.");
    } finally {
      setCreatingPayment(false);
    }
  };

  return (
    <>
      <FullBleedViewport>
        <BalanceState>
          <Typography variant="overline" color="text.secondary">
            Баланс
          </Typography>
          {loading ? (
            <CircularProgress />
          ) : (
            <Typography variant="h3" component="p">
              {balance !== null ? formatBalls(balance) : "—"}
            </Typography>
          )}
        </BalanceState>
      </FullBleedViewport>

      <BottomBar>
        <BarTile>
          <TextField
            size="small"
            label="Сумма, ₽"
            type="number"
            value={amount}
            sx={{ width: 140 }}
            onChange={(event) => setAmount(event.target.value)}
            inputProps={{ min: 1 }}
          />
        </BarTile>
        <BarTile>
          <TextField
            size="small"
            label="Метод оплаты"
            select
            value={method}
            sx={{ minWidth: 150 }}
            onChange={(event) => setMethod(event.target.value)}
          >
            {paymentMethods.map((paymentMethod) => (
              <MenuItem key={paymentMethod.value} value={paymentMethod.value}>
                {paymentMethod.label}
              </MenuItem>
            ))}
          </TextField>
        </BarTile>
        <BarTile>
          <Button
            variant="contained"
            size="small"
            onClick={handlePay}
            disabled={creatingPayment}
          >
            {creatingPayment ? "Создание платежа..." : "Оплатить"}
          </Button>
        </BarTile>
      </BottomBar>

      <Snackbar
        open={Boolean(error)}
        autoHideDuration={5000}
        onClose={() => setError(null)}
      >
        <Alert severity="error" onClose={() => setError(null)}>
          {error}
        </Alert>
      </Snackbar>
    </>
  );
};

export default Billing;
